import React from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

interface PricingTierProps {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  popular?: boolean;
  image: string;
}

const PricingTier: React.FC<PricingTierProps> = ({
  name,
  price,
  period,
  description,
  features,
  popular = false,
  image,
}) => {
  return (
    <div
      className={`relative bg-white dark:bg-slate-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 ${
        popular
          ? 'border-purple-500 dark:border-purple-400 scale-105'
          : 'border-gray-200 dark:border-slate-700'
      } overflow-hidden`}
    >
      {popular && (
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-center py-2 text-sm font-semibold">
          Most Popular
        </div>
      )}
      <div className="relative h-48 overflow-hidden">
        <img
          src={image}
          alt={name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-2xl font-bold text-white mb-1">{name}</h3>
          <div className="flex items-baseline">
            <span className="text-4xl font-bold text-white">{price}</span>
            <span className="text-gray-300 ml-2">{period}</span>
          </div>
        </div>
      </div>
      <div className="p-6">
        <p className="text-gray-600 dark:text-gray-300 mb-6 min-h-[60px]">
          {description}
        </p>
        <ul className="space-y-3 mb-8">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <Check className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5 mr-3" />
              <span className="text-gray-700 dark:text-gray-300">{feature}</span>
            </li>
          ))}
        </ul>
        <Link
          to="/contact-sales?source=pricing-section"
          className={`block w-full text-center py-3 px-6 rounded-lg font-semibold transition-colors ${
            popular
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700'
              : 'bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-slate-600'
          }`}
        >
          Get Started
          <ArrowRight className="inline-block ml-2 w-4 h-4" />
        </Link>
      </div>
    </div>
  );
};

const PricingSection: React.FC = () => {
  const pricingTiers = [
    {
      name: 'Starter',
      price: '$5',
      period: '/employee/month',
      description: 'Ideal for compact teams beginning their wellness journey.',
      features: [
        'Up to 100 employees',
        'Basic step challenges',
        'Activity tracking',
        'Leaderboards',
        'Email support',
        'Mobile & web access',
      ],
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop',
    },
    {
      name: 'Professional',
      price: '$8',
      period: '/employee/month',
      description: 'Perfect for expanding organizations requiring comprehensive wellness solutions.',
      features: [
        'Up to 500 employees',
        'All challenge types',
        'Team challenges',
        'Custom challenges',
        'Analytics dashboard',
        'Rewards & incentives',
        'Priority support',
        'API access',
      ],
      popular: true,
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop',
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      description: 'Customized offerings for major organizations with unique needs.',
      features: [
        'Unlimited employees',
        'All features included',
        'White-label options',
        'Dedicated account manager',
        'Custom integrations',
        'Advanced analytics',
        '24/7 support',
        'SLA guarantee',
        'On-premise deployment option',
      ],
      image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=600&h=400&fit=crop',
    },
  ];

  return (
    <section id="pricing" className="py-20 bg-gradient-to-br from-gray-50 to-purple-50 dark:from-slate-800 dark:to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Clear, Straightforward Pricing
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Select the package that matches your organization. Every package includes our essential wellness capabilities.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pricingTiers.map((tier, index) => (
            <PricingTier key={index} {...tier} />
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Require a personalized solution? We're ready to assist.
          </p>
          <Link
            to="/contact-sales?source=pricing-cta"
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all"
          >
            Contact Sales
            <ArrowRight className="ml-2 w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;

